import type { Uuid } from "@/domains/global/identifiers";
import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import { resolveAssistantTaskNoteReferenceSyncModelUuid } from "@/modules/uuid/sync-models/resolve-assistant-task-note-reference-sync-model-uuid";
import type { AssistantSyncModelObservable } from "@/store/assistant/AssistantSyncModelObservable";
import type {
  AssistantTaskModelData,
  AssistantTaskNoteReferenceModelData,
} from "@/store/assistant/types";
import type { AssistantTaskObservable } from "@/store/assistant/AssistantTaskObservable";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import {
  buildAssistantTaskNoteReferenceOptimisticDelete,
  buildAssistantTaskNoteReferenceOptimisticUpsert,
} from "@/store/sync/operations/helpers/assistant-tasks/build-assistant-task-note-reference-optimistic-updates";
import type { IUpdateAssistantTaskOperation } from "@/store/sync/operations/types";
import type { OptimisticSyncUpdate, SyncModelData } from "@/store/sync/types";
import { getLoggableOperation } from "@/store/sync/utils";

const hasPayloadField = (
  payload: IUpdateAssistantTaskOperation["payload"],
  field: keyof IUpdateAssistantTaskOperation["payload"]
) => Object.prototype.hasOwnProperty.call(payload, field);

export class UpdateAssistantTaskOperation extends BaseSyncOperation<IUpdateAssistantTaskOperation> {
  get operationKind(): "UPDATE_ASSISTANT_TASK" {
    return "UPDATE_ASSISTANT_TASK";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const existingTask = await this.store.assistantTasks.getAsync(this.payload.id);
    if (!existingTask) {
      logger.error({
        message: "Attempted to update assistant task that does not exist",
        info: {
          operation: getLoggableOperation(this),
        },
      });
      return [];
    }

    const updates: OptimisticSyncUpdate<SyncModelData>[] = [];
    if (this.hasTaskFieldChanges()) {
      updates.push(this.generateTaskUpdate({ existingTask }));
    }

    const existingReferencesById = new Map<
      Uuid,
      AssistantSyncModelObservable<AssistantTaskNoteReferenceModelData>
    >();
    if (this.changedReferencedNoteIds.length > 0) {
      const referenceIds = this.changedReferencedNoteIds.map((noteId) =>
        this.resolveNoteReferenceId({ noteId })
      );
      for (const reference of await this.store.assistantTaskNoteReferences.getManyAsync(
        referenceIds
      )) {
        existingReferencesById.set(reference.id, reference);
      }
    }

    for (const noteId of this.addedReferencedNoteIds) {
      const referenceId = this.resolveNoteReferenceId({ noteId });
      if (existingReferencesById.has(referenceId)) {
        continue;
      }
      updates.push(
        buildAssistantTaskNoteReferenceOptimisticUpsert({
          assistantId: existingTask.modelData.assistant_id,
          committedAt: this.committedAt,
          modelScopes: existingTask.modelScopes,
          noteId,
          optimisticUpdateId: uuidModule.generate(),
          spaceAccountId: existingTask.modelData.space_account_id,
          taskId: this.payload.id,
        })
      );
    }

    for (const noteId of this.removedReferencedNoteIds) {
      const referenceId = this.resolveNoteReferenceId({ noteId });
      const reference = existingReferencesById.get(referenceId);
      if (!reference) {
        continue;
      }
      updates.push(
        buildAssistantTaskNoteReferenceOptimisticDelete({
          committedAt: this.committedAt,
          modelId: referenceId,
          modelVersion: reference.modelVersion,
          optimisticUpdateId: uuidModule.generate(),
        })
      );
    }

    return updates;
  }

  async triggerRecompute(): Promise<void> {
    if (this.hasTaskFieldChanges()) {
      await this.store.assistantTasks.recompute(this.payload.id);
    }
    if (this.changedReferencedNoteIds.length > 0) {
      await this.store.assistantTaskNoteReferences.bulkRecompute(
        this.changedReferencedNoteIds.map((noteId) => this.resolveNoteReferenceId({ noteId }))
      );
    }
  }

  private generateTaskUpdate({
    existingTask,
  }: {
    existingTask: AssistantTaskObservable;
  }): OptimisticSyncUpdate<AssistantTaskModelData> {
    const modelData = existingTask.modelData;
    const taskUpdate = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.id,
        model_kind: "ASSISTANT_TASK",
        model_version: existingTask.modelVersion,
        model_data: {
          ...modelData,
          title: this.payload.title ?? modelData.title,
          description: hasPayloadField(this.payload, "description")
            ? (this.payload.description ?? null)
            : modelData.description,
          status: this.payload.status ?? modelData.status,
          due_at:
            this.payload.clear_due_at || hasPayloadField(this.payload, "due_at")
              ? (this.payload.due_at ?? null)
              : modelData.due_at,
          project_id:
            this.payload.clear_project_id || hasPayloadField(this.payload, "project_id")
              ? (this.payload.project_id ?? null)
              : modelData.project_id,
          updated_at: this.committedAt,
        },
        model_scopes: existingTask.modelScopes,
      },
    } as unknown as OptimisticSyncUpdate<AssistantTaskModelData>;

    return taskUpdate;
  }

  private resolveNoteReferenceId({ noteId }: { noteId: Uuid }): Uuid {
    return resolveAssistantTaskNoteReferenceSyncModelUuid({
      taskId: this.payload.id,
      noteId,
    });
  }

  private get addedReferencedNoteIds(): Uuid[] {
    return [...new Set(this.payload.add_referenced_note_ids ?? [])];
  }

  private get removedReferencedNoteIds(): Uuid[] {
    return [...new Set(this.payload.remove_referenced_note_ids ?? [])];
  }

  private get changedReferencedNoteIds(): Uuid[] {
    return [...new Set([...this.addedReferencedNoteIds, ...this.removedReferencedNoteIds])];
  }

  private hasTaskFieldChanges(): boolean {
    return (
      hasPayloadField(this.payload, "title") ||
      hasPayloadField(this.payload, "description") ||
      hasPayloadField(this.payload, "status") ||
      hasPayloadField(this.payload, "due_at") ||
      this.payload.clear_due_at === true ||
      hasPayloadField(this.payload, "project_id") ||
      this.payload.clear_project_id === true
    );
  }
}
